import React from "react"
import { useNavigate, useSearchParams } from "react-router-dom"
import { useWebsiteProducts } from "@/hooks/useWebsiteProducts"
import { useWebsiteConfig } from "@/hooks/useWebsiteConfig"
import { SlidersHorizontal, X } from "lucide-react"
import { FilterSidebar } from "../components/FilterSidebar"
import ProductGroup from "../components/ProductGroup"

type HomeProps = {
  tagId?: number
  initialCategoryId?: number
  showHero?: boolean
}

export const Home: React.FC<HomeProps> = ({
  tagId,
  initialCategoryId,
  showHero = true,
}) => {
  const [selectedSizes, setSelectedSizes] = React.useState<number[]>([])
  const [selectedColors, setSelectedColors] = React.useState<number[]>([])
  const [selectedCategories, setSelectedCategories] = React.useState<number[]>(
    initialCategoryId ? [initialCategoryId] : []
  )
  const [filtersOpen, setFiltersOpen] = React.useState(false)
  const [searchParams] = useSearchParams()
  const navigate = useNavigate()
  const search = searchParams.get("search") ?? ""

  const { data: websiteConfig } = useWebsiteConfig()

  const { data, isLoading, fetchNextPage, hasNextPage, isFetchingNextPage } =
    useWebsiteProducts({
      per_page: 32,
      tag_id: tagId,
      size_id: selectedSizes,
      color_id: selectedColors,
      category_id: selectedCategories,
      search: search || undefined,
    })

  const clearNavbarTag = () => {
    const params = new URLSearchParams()
    if (search) params.set("search", search)

    navigate({
      pathname: "/",
      search: params.toString() ? `?${params.toString()}` : "",
    })
  }

  const activeFilterCount =
    selectedSizes.length + selectedColors.length + selectedCategories.length

  // Lock page scroll while the mobile filter drawer is open
  React.useEffect(() => {
    if (!filtersOpen) return

    const desktop = window.matchMedia("(min-width: 1024px)")
    const closeOnDesktop = () => desktop.matches && setFiltersOpen(false)

    document.body.style.overflow = "hidden"
    desktop.addEventListener("change", closeOnDesktop)

    return () => {
      document.body.style.overflow = ""
      desktop.removeEventListener("change", closeOnDesktop)
    }
  }, [filtersOpen])

  // Observer for infinite scroll
  const observerTarget = React.useRef<HTMLDivElement>(null)

  React.useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && hasNextPage) {
          fetchNextPage()
        }
      },
      { threshold: 1.0 }
    )

    if (observerTarget.current) {
      observer.observe(observerTarget.current)
    }

    return () => {
      if (observerTarget.current) {
        observer.unobserve(observerTarget.current)
      }
    }
  }, [observerTarget, hasNextPage, fetchNextPage])

  // Flatten pages
  const products = data?.pages.flatMap((page: any) => page.data.data) || []

  const handleShopNow = () => {
    const productsSection = document.getElementById("products-section")
    if (productsSection) {
      productsSection.scrollIntoView({ behavior: "smooth" })
    }
  }
  return (
    <div className="min-h-screen bg-gray-50">
      {showHero && (
        <div className="relative bg-gray-900 text-white">
          <div className="absolute inset-0 overflow-hidden">
            <img
              src={
                websiteConfig?.banner_image ||
                "https://images.unsplash.com/photo-1441986300917-64674bd600d8?auto=format&fit=crop&w=1600&q=80"
              }
              alt="Hero Banner"
              className="w-full h-full object-cover opacity-50"
            />
          </div>
          <div className="relative max-w-7xl mx-auto py-24 px-4 sm:py-32 sm:px-6 lg:px-8 text-center">
            <h1 className="text-4xl font-extrabold tracking-tight sm:text-5xl lg:text-6xl">
              {websiteConfig?.title || "New Season Arrivals"}
            </h1>
            <p className="mt-6 text-xl max-w-3xl mx-auto">
              {websiteConfig?.description ||
                "Check out the latest trends in fashion. High quality materials, sustainable sourcing, and modern designs."}
            </p>
            <div className="mt-10">
              <button
                onClick={handleShopNow}
                className="bg-indigo-600 px-8 py-3 rounded-md font-medium hover:bg-indigo-700 transition"
              >
                Shop Now
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Featured Products */}
      <div
        id="products-section"
        className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16"
      >
        {/* <h2 className="text-2xl font-bold text-gray-900 mb-8">Trending Now</h2> */}

        {!showHero && (
          <h2 className="mb-8 text-2xl font-bold text-gray-900">Products</h2>
        )}

        <button
          onClick={() => setFiltersOpen(true)}
          className="mb-6 inline-flex items-center gap-2 rounded-md border bg-white px-4 py-2 text-sm font-medium text-gray-700 shadow-sm hover:bg-gray-50 lg:hidden"
        >
          <SlidersHorizontal className="h-4 w-4" />
          Filters
          {activeFilterCount > 0 && (
            <span className="rounded-full bg-indigo-600 px-2 py-0.5 text-xs text-white">
              {activeFilterCount}
            </span>
          )}
        </button>

        <div className="grid grid-cols-1 lg:grid-cols-4 gap-8">
          {/* Filters: static sidebar on desktop, slide-over drawer on mobile */}
          <div
            className={`${
              filtersOpen ? "fixed inset-0 z-[60] flex" : "hidden"
            } lg:static lg:z-auto lg:block`}
          >
            <div
              className="absolute inset-0 bg-black/40 lg:hidden"
              onClick={() => setFiltersOpen(false)}
            />
            <div className="relative flex h-full w-80 max-w-[85vw] flex-col bg-white shadow-xl lg:h-auto lg:w-auto lg:max-w-none lg:bg-transparent lg:shadow-none">
              <div className="flex items-center justify-between border-b px-5 py-4 lg:hidden">
                <h3 className="text-lg font-semibold text-gray-900">Filters</h3>
                <button
                  onClick={() => setFiltersOpen(false)}
                  aria-label="Close filters"
                  className="rounded p-1 text-gray-500 hover:bg-gray-100"
                >
                  <X className="h-5 w-5" />
                </button>
              </div>
              <div className="flex-1 overflow-y-auto lg:overflow-visible">
                <FilterSidebar
                  selectedSizes={selectedSizes}
                  setSelectedSizes={setSelectedSizes}
                  selectedColors={selectedColors}
                  setSelectedColors={setSelectedColors}
                  selectedCategories={selectedCategories}
                  setSelectedCategories={setSelectedCategories}
                  activeTagId={tagId}
                  activeTagCategoryId={initialCategoryId}
                  onClearTag={tagId ? clearNavbarTag : undefined}
                />
              </div>
              <div className="border-t p-4 lg:hidden">
                <button
                  onClick={() => setFiltersOpen(false)}
                  className="w-full rounded-md bg-indigo-600 py-2.5 text-sm font-medium text-white hover:bg-indigo-700"
                >
                  Show Results
                </button>
              </div>
            </div>
          </div>
          {isLoading ? (
            <div className="text-center py-12 lg:col-span-3">
              <div className="text-gray-500">Loading products...</div>
            </div>
          ) : products.length === 0 ? (
            <div className="lg:col-span-3 text-center py-12">
              <div className="text-gray-500">No products available</div>
            </div>
          ) : (
            <div className="lg:col-span-3">
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-8">
                {products.map((product) => (
                  <ProductGroup product={product} key={product.id} />
                ))}
              </div>

              {/* Infinite Scroll Target */}
              <div
                ref={observerTarget}
                className="mt-12 text-center h-10 w-full flex justify-center items-center"
              >
                {isFetchingNextPage && (
                  <div className="text-gray-500">Loading more...</div>
                )}
                {!hasNextPage && products.length > 0 && (
                  <div className="text-gray-400 text-sm">No more products</div>
                )}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  )
}
